"use client";

import { useEffect, useState } from 'react';
import { Box, Chip, Container, FormControl, IconButton, InputAdornment, OutlinedInput, Skeleton, Stack, TablePagination } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import KoleksiCard from '@/components/KoleksiCard';
import color from '@/style/theme/Color';
import { usePathname, useRouter } from 'next/navigation';
import FilterKoleksiDialog from './FilterKoleksiDialog';
import { getKoleksi, GetKoleksiProps } from '@/utils/getKoleksi';
import FilterConfigurationBox from "@/app/(user)/koleksi/FilterConfigurationBox";
import { JenisPustaka, jenisPustakaLabels } from '@/types/shared.types';

const KoleksiBahanPustaka = ({
  initialParams = {},
  initialKoleksi = []
}: {
  initialParams: {
    keyword?: string;
    page?: number;
    rowsPerPage?: number;
  },
  initialKoleksi: any,
}) => {
  const [koleksi, setKoleksi] = useState(initialKoleksi);
  const [keyword, setKeyword] = useState(initialParams?.keyword || '');
  const [page, setPage] = useState(initialParams?.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(initialParams?.rowsPerPage || 10);
  const [buttonIsActive, setButtonIsActive] = useState({
    relevan: true,
    terbaru: false,
    banyakDibaca: false
  });
  const [jenisPustakaSelected, setJenisPustakaSelected] = useState({
    id: '0',
    label: 'Semua'
  });
  const [klasifikasiSelected, setKlasifikasiSelected] = useState<string[]>([]);
  const [klasifikasiList, setKlasifikasiList] = useState<{ id: string; nama: string; }[] | null>(null);
  const [rangeTahun, setRangeTahun] = useState<[number, number]>([1960, new Date().getFullYear()]);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [getKoleksiTrigger, setGetKoleksiTrigger] = useState(false);

  const jenisPustaka = (Object.keys(JenisPustaka) as (keyof typeof JenisPustaka)[]).map((key) => ({
    id: jenisPustakaLabels[key],
    label: jenisPustakaLabels[key]
  }))

  const router = useRouter();
  const url = usePathname()?.split('/');
  const idSharedKoleksi = url?.[url.length - 1] || '';

  useEffect(() => {
    if (page !== initialParams.page || koleksi !== initialKoleksi) {
      fetchKoleksi();
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    console.log(klasifikasiSelected);
  }, [klasifikasiSelected]);

  const fetchKoleksi = async () => {
    try {

      const props: GetKoleksiProps = {
        start: page * rowsPerPage,
        size: rowsPerPage,
        globalFilter: keyword,
        filters: []
      }

      if (jenisPustakaSelected.id !== '0') {
        props.jenisPustaka = jenisPustakaSelected.id;
      }

      if (klasifikasiSelected.length > 0) {
        props.filters!.push({ id: 'klasifikasiId', value: klasifikasiSelected.map(k => parseInt(k)) });
      }

      if (rangeTahun[0] > 1960 || rangeTahun[1] < new Date().getFullYear()) {
        props.filters!.push({ 
          id: 'tahunTerbit', 
          value: [
            `${rangeTahun[0]}-01-01T00:00:00Z`,
            `${rangeTahun[1]}-12-31T23:59:59Z`
          ] });
      }

      if (buttonIsActive.terbaru) {
        props.sorting = [{ id: 'createdAt', desc: true }];
      // } else if (buttonIsActive.banyakDibaca) {
      //   props.sorting = [{ id: '', desc: true }];
      // } else {
      //   props.sorting = [{ id: 'relevan', desc: false }];
      }

      const result = await getKoleksi(props);
      setKoleksi(result);
    } catch (error) {
      console.error(error);
    }
  };

  const toggleFilterKoleksi = () => {
    setOpenFilterDialog(true);
  };

  const onCloseFilterDialog = (value: object) => {
    setOpenFilterDialog(false);
    //   TODO
  };

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();

  const skeletonCard = () => {
    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack>
          <Skeleton variant="text" sx={{ fontSize: '12px' }} />
          <Skeleton variant="text" sx={{ fontSize: '16px' }} />
          <Skeleton variant="text" sx={{ fontSize: '14px' }} />
        </Stack>
        <Stack textAlign="center">
          <Skeleton variant="text" sx={{ fontSize: '12px' }} />
          <Skeleton variant="text" sx={{ fontSize: '12px' }} />
          <Skeleton variant="text" sx={{ fontSize: '12px' }} />
          <Skeleton variant="text" sx={{ fontSize: '12px' }} />
        </Stack>
      </Stack>
    );
  };

  const buildSearchParams = () => {
    let searchParams = new URLSearchParams();
    const params = {
      q: keyword,
      page: page,
      rowsPerPage: rowsPerPage,
    }
    for (const key in params) {
      if (params[key] !== undefined && params[key] !== null) {
        searchParams.set(key, params[key]);
      }
    }
    return searchParams.toString();
  };

  const handleEnterSubmit = (event) => {
    if (event.code === 'Enter') {
      updateKoleksi();
    }
  };

  const updateKoleksi = () => {
    router.replace('/koleksi?' + buildSearchParams());
    fetchKoleksi();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClickUrutan = (value) => {
    setButtonIsActive({
      relevan: value === 'relevan',
      terbaru: value === 'terbaru',
      banyakDibaca: value === 'banyak_dibaca'
    });
  };

  const handleDeleteUrutanChip = () => {
    setButtonIsActive({ relevan: true, terbaru: false, banyakDibaca: false });
    setGetKoleksiTrigger((prevChipTrigger) => !prevChipTrigger);
  };

  const handleDeleteBPChip = () => {
    setJenisPustakaSelected(jenisPustaka[0]);
    setKlasifikasiSelected([]);
    setGetKoleksiTrigger((prevChipTrigger) => !prevChipTrigger);
  };

  const handleDeleteKlasifikasiChip = (event, id) => {
    setKlasifikasiSelected((prevKlasifikasiSelected) => {
      return prevKlasifikasiSelected.filter((k, i) => i !== id);
    });
    setGetKoleksiTrigger((prevChipTrigger) => !prevChipTrigger);
  };

  const handleDeleteTahunChip = () => {
    setRangeTahun([1960, currentYear]);
    setGetKoleksiTrigger((prevChipTrigger) => !prevChipTrigger);
  };

  return <>
    <FilterKoleksiDialog
      open={openFilterDialog}
      onClose={onCloseFilterDialog}
      buttonIsActive={buttonIsActive}
      setButtonIsActive={setButtonIsActive}
      jenis_pustaka={jenisPustaka}
      jenisPustakaSelected={jenisPustakaSelected}
      setJenisPustakaSelected={setJenisPustakaSelected}
      klasifikasiSelected={klasifikasiSelected}
      setKlasifikasiSelected={setKlasifikasiSelected}
      klasifikasiList={klasifikasiList}
      setKlasifikasiList={setKlasifikasiList}
      rangeTahun={rangeTahun}
      setRangeTahun={setRangeTahun}
    />
    <Box
      sx={{
        backgroundColor: color.dark_white,
        paddingY: '20px'
      }}
    >
      <Container maxWidth="lg">
        <FormControl sx={{ width: '100%' }}>
          <OutlinedInput
            placeholder="Telusuri"
            size="small"
            color="primary"
            value={keyword}
            onKeyUp={handleEnterSubmit}
            onChange={(e) => setKeyword(e.currentTarget.value)}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.8)'
              },
              '&.Mui-focused': {
                backgroundColor: 'rgba(255, 255, 255, 1)'
              }
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={updateKoleksi}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
      </Container>
    </Box>
    <Box sx={{
      display: { xs: 'block', md: 'none' },
      marginX: 2
    }}>
      <Chip
        label="Tambah Filter"
        variant="filled"
        icon={<AddCircleIcon />}
        onClick={toggleFilterKoleksi}
        clickable
        sx={{ marginTop: '10px', marginRight: '5px' }}
      />
      {buttonIsActive.terbaru && (
        <Chip
          label="Terbaru"
          variant="filled"
          color="primary"
          onDelete={handleDeleteUrutanChip}
          onClick={handleDeleteUrutanChip}
          clickable
          sx={{ marginTop: '10px', marginRight: '5px' }}
        />
      )}
      {buttonIsActive.banyakDibaca && (
        <Chip
          label="Paling Banyak Dibaca"
          variant="filled"
          color="primary"
          onDelete={handleDeleteUrutanChip}
          onClick={handleDeleteUrutanChip}
          clickable
          sx={{ marginTop: '10px', marginRight: '5px' }}
        />
      )}
      {jenisPustakaSelected !== null &&
        jenisPustakaSelected.label !== 'Semua' && (
          <Chip
            label={jenisPustakaSelected.label}
            variant="filled"
            color="primary"
            onDelete={handleDeleteBPChip}
            onClick={handleDeleteBPChip}
            clickable
            sx={{ marginTop: '10px', marginRight: '5px' }}
          />
        )}
      {klasifikasiList !== null && klasifikasiSelected.length !== 0 && (
        <>
          {klasifikasiSelected.map(
            (item, key) =>
              klasifikasiSelected[key] !== '' && (
                <Chip
                  id={key.toString()}
                  key={key}
                  label={klasifikasiList[key].nama}
                  variant="filled"
                  color="primary"
                  onDelete={(e) => handleDeleteKlasifikasiChip(e, key)}
                  onClick={(e) => handleDeleteKlasifikasiChip(e, key)}
                  clickable
                  sx={{ marginTop: '10px', marginRight: '5px' }}
                />
              )
          )}
        </>
      )}
      {(rangeTahun[0] > 1960 || rangeTahun[1] < currentYear) && (
        <Chip
          label={rangeTahun[0] + '-' + rangeTahun[1]}
          variant="filled"
          color="primary"
          onDelete={handleDeleteTahunChip}
          onClick={handleDeleteTahunChip}
          clickable
          sx={{ marginTop: '10px', marginRight: '5px' }}
        />
      )}
    </Box>

    <Box sx={{ backgroundColor: color.light_gray }}>
      <Container
        maxWidth="lg"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'auto', md: '341px auto' },
          paddingY: '40px',
          gap: '2rem'
        }}
      >
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <FilterConfigurationBox
            buttonIsActive={buttonIsActive}
            setButtonIsActive={setButtonIsActive}
            jenisPustaka={jenisPustaka}
            jenisPustakaSelected={jenisPustakaSelected}
            setJenisPustakaSelected={setJenisPustakaSelected}
            klasifikasiSelected={klasifikasiSelected}
            setKlasifikasiSelected={setKlasifikasiSelected}
            klasifikasiList={klasifikasiList}
            setKlasifikasiList={setKlasifikasiList}
            rangeTahun={rangeTahun}
            setRangeTahun={setRangeTahun}
            onApplyFilter={updateKoleksi}
          />
        </Box>
        <Box
          sx={{
            marginX: -2,
            marginY: -2,
          }}
        >
          {koleksi.rows && koleksi.rows.map((element, key) => (
            <KoleksiCard
              key={key}
              id={element.id}
              namaJenisPustaka={element.jenisPustaka}
              namaKlasifikasi={element.klasifikasi}
              judul={element.judul}
              penulis={element.penulis}
              jumlahDibaca={element.jumlahDibaca}
              jumlahDisimpan={element.jumlahDisimpan}
              isDapatDipinjam={element.isDapatDipinjam}
              isDapatDibaca={element.isDapatDibaca}
            />
          ))}
          {koleksi === null && (
            <>
              {Array.from({ length: 5 }).map((_, index) => (
                <Stack
                  key={index}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ marginBottom: '40px' }}
                >
                  <Stack>
                    <Skeleton
                      variant="text"
                      width={250}
                      sx={{ fontSize: '12px' }}
                    />
                    <Skeleton
                      variant="text"
                      width={261}
                      sx={{ fontSize: '16px' }}
                    />
                    <Skeleton
                      variant="text"
                      width={220}
                      sx={{ fontSize: '14px' }}
                    />
                  </Stack>
                  <Stack alignItems="center">
                    <Skeleton
                      variant="text"
                      width={20}
                      sx={{ fontSize: '12px' }}
                    />
                    <Skeleton
                      variant="text"
                      width={60}
                      sx={{ fontSize: '12px' }}
                    />
                    <Skeleton
                      variant="text"
                      width={20}
                      sx={{ fontSize: '12px' }}
                    />
                    <Skeleton
                      variant="text"
                      width={60}
                      sx={{ fontSize: '12px' }}
                    />
                  </Stack>
                </Stack>
              ))}
            </>
          )}
          <Stack direction="row" justifyContent="center">
            <TablePagination
              component="div"
              count={koleksi?.count ?? 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Stack>
        </Box>
      </Container>
    </Box>
  </>;
};

export default KoleksiBahanPustaka;
